import { computed, ref, watch, type Ref } from 'vue';
import {
  getGradeModifier,
  gradeWithModifier,
  isValidSchoolGrade,
  parseSchoolGrade,
  type ISchoolGradeConfiguration,
} from 'common/utils/school.grade.utils';
import { roundTwoDecimals } from 'common/utils/math.utils';
import { useSchoolGradeCollapse } from './useSchoolGradeCollapse';

interface ISchoolGradeEditorOptions {
  value: Readonly<Ref<number | null>>;
  config: Readonly<Ref<ISchoolGradeConfiguration>>;
  canEdit: Readonly<Ref<boolean>>;
  save: (value: number | null) => void;
}

const DECIMAL_GRADE_DRAFT_PATTERN = /^\d(?:[.,]\d{0,2})?$/;
const MINIMUM_GRADE_PREFIX_PATTERN = /^0(?:[.,]7?)?$/;
const TRAILING_DECIMAL_SEPARATOR_PATTERN = /[.,]$/;

const isAllowedDraft = (next: string, config: ISchoolGradeConfiguration): boolean => {
  if (next === '') return true;
  if (!DECIMAL_GRADE_DRAFT_PATTERN.test(next)) return false;
  // Incomplete prefixes are needed to type the lower endpoint, 0.75.
  return MINIMUM_GRADE_PREFIX_PATTERN.test(next) || isValidSchoolGrade(Number(next.replace(',', '.')), config);
};

const useDecimalDraft = ({ value, config, canEdit }: ISchoolGradeEditorOptions) => {
  const selectedGrade = ref<number | null>(null);
  const editing = computed(() => selectedGrade.value != null);
  const displayValue = computed(() => selectedGrade.value ?? value.value);
  const draft = ref('');
  const begin = (grade: number): void => {
    selectedGrade.value = grade;
    draft.value = String(grade);
  };
  const cancel = (): void => {
    selectedGrade.value = null;
  };
  const accepts = (next: string): boolean => editing.value && canEdit.value && isAllowedDraft(next, config.value);
  const update = (next: string): boolean => {
    if (!accepts(next)) return false;
    draft.value = next;
    return true;
  };
  const commit = (save: (value: number | null) => void): void => {
    if (!editing.value) return;
    const empty = !draft.value.trim();
    const parsed = empty ? null : parseSchoolGrade(draft.value.replace(TRAILING_DECIMAL_SEPARATOR_PATTERN, ''));
    cancel();
    if (!empty && (parsed == null || !isValidSchoolGrade(parsed, config.value))) return;
    save(parsed);
  };
  return { displayValue, editing, draft, begin, cancel, accepts, update, commit };
};

const getModifierValue = (value: number | null, modifier: -1 | 1, config: ISchoolGradeConfiguration): number | null => {
  if (value == null) return null;
  if (config.decimalGradesEnabled) return roundTwoDecimals(value + modifier * 0.25);
  if (!config.gradeModifiersEnabled) return null;
  const notation = getGradeModifier(value, config.lowerGradeValueIsBetter);
  if (!notation) return null;
  return gradeWithModifier(
    notation.base,
    notation.modifier === modifier ? 0 : modifier,
    config.lowerGradeValueIsBetter,
  );
};

const useSchoolGradeChoices = (
  options: ISchoolGradeEditorOptions,
  decimal: Pick<ReturnType<typeof useDecimalDraft>, 'displayValue' | 'begin'>,
  collapse: ReturnType<typeof useSchoolGradeCollapse>,
) => {
  const { config, canEdit, save } = options;
  const { displayValue } = decimal;
  const notation = computed(() =>
    displayValue.value == null ? null : getGradeModifier(displayValue.value, config.value.lowerGradeValueIsBetter),
  );
  const selectedBase = computed(() =>
    displayValue.value == null
      ? null
      : Math.max(1, Math.min(config.value.gradeLevels.length, Math.round(displayValue.value))),
  );
  const commit = (next: number | null): void => {
    save(next);
    collapse.schedule();
  };
  const choose = (grade: number): void => {
    if (!canEdit.value) return;
    if (config.value.decimalGradesEnabled) {
      collapse.stop();
      decimal.begin(selectedBase.value === grade && displayValue.value != null ? displayValue.value : grade);
      return;
    }
    if (collapse.collapsed.value) {
      collapse.collapsed.value = false;
      collapse.stop();
      return;
    }
    const selectedValueIsAllowed = displayValue.value != null && isValidSchoolGrade(displayValue.value, config.value);
    commit(selectedBase.value === grade && selectedValueIsAllowed ? null : grade);
  };
  const modifierDisabled = (modifier: -1 | 1): boolean => {
    const next = getModifierValue(displayValue.value, modifier, config.value);
    return !canEdit.value || next == null || !isValidSchoolGrade(next, config.value);
  };
  const applyModifier = (modifier: -1 | 1): void => {
    if (modifierDisabled(modifier)) return;
    if (!config.value.decimalGradesEnabled) collapse.modifierOpen.value = false;
    commit(getModifierValue(displayValue.value, modifier, config.value));
  };
  return { notation, selectedBase, commit, choose, modifierDisabled, applyModifier };
};

/** UI actions work on a separate draft and commit through the value model's single save action. */
export const useSchoolGradeEditor = (options: ISchoolGradeEditorOptions) => {
  const { config, canEdit } = options;
  const decimal = useDecimalDraft(options);
  const collapse = useSchoolGradeCollapse(decimal.displayValue, decimal.editing);
  const choices = useSchoolGradeChoices(options, decimal, collapse);
  const cancelInteraction = (): void => {
    collapse.stop();
    decimal.cancel();
    collapse.modifierOpen.value = false;
  };
  watch(
    [
      () => config.value.decimalGradesEnabled,
      () => config.value.gradeModifiersEnabled,
      () => config.value.lowerGradeValueIsBetter,
      () => config.value.gradeLevels.map((level) => level.level).join(','),
    ],
    cancelInteraction,
  );
  watch(canEdit, (allowed) => {
    if (!allowed) cancelInteraction();
  });
  return {
    displayValue: decimal.displayValue,
    editing: decimal.editing,
    draft: decimal.draft,
    collapsed: collapse.collapsed,
    animateScale: collapse.animateScale,
    modifierOpen: collapse.modifierOpen,
    notation: choices.notation,
    selectedBase: choices.selectedBase,
    commit: choices.commit,
    choose: choices.choose,
    acceptsDraft: decimal.accepts,
    updateDraft: decimal.update,
    commitDraft: () => decimal.commit(choices.commit),
    cancelDraft: decimal.cancel,
    modifierDisabled: choices.modifierDisabled,
    applyModifier: choices.applyModifier,
  };
};
